import { useEffect, useState } from 'react';
import { personsApi } from './personnelApi';
import type { PersonIdentity } from './personnelTypes';

/**
 * The name and the date of birth from the identity store — `personnel.reveal`, audited on every
 * call.
 *
 * **Fetched when the caller mounts, and only while `enabled`.** The caller decides that: it is
 * the reader's key AND the record not being erased (the store answers an erased one with a 404,
 * and the surface already says why). A reader without the key makes no request at all.
 *
 * Two surfaces draw these fields — `Basic info` and the `PDS` — and each opening writes its own
 * access row, which is the trade taken on 15 Sep 2026: a reveal row records a page that
 * genuinely showed the name, not a click. One hook rather than two copies of the state, so the
 * two cannot disagree about when a reveal happens.
 *
 * `retry` re-runs the request after a failure. Nothing reads the counter behind it — bumping it
 * IS the request, which keeps one fetch path.
 */
export function usePersonIdentity(personId: string, enabled: boolean) {
  const [identity, setIdentity] = useState<PersonIdentity | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled) return;

    const controller = new AbortController();

    setIdentity(null);
    setFailed(false);

    personsApi
      .reveal(personId, controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setIdentity(next);
      })
      .catch(() => {
        // Nothing else on the surface depends on these rows, so a failure here must not take
        // it down: they say so, and the rest still reads.
        if (!controller.signal.aborted) setFailed(true);
      });

    return () => controller.abort();
  }, [enabled, personId, attempt]);

  return { identity, failed, retry: () => setAttempt((n) => n + 1) };
}
